'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Formik, FormikHelpers } from 'formik';

import { CreateDonationRequestModeEnum, Project, Reward, Subproject, User } from '@/app/api';
import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { useCommercialProjectDonationFormValidationSchema } from '@/app/components/DonationForm/useDonationFormValidationSchema';
import { SelectOption } from '@/app/components/Select/Select';
import { REWARD_SEARCH_PARAM_KEY, REWARD_VARIANT_SEARCH_PARAM_KEY, SUBPROJECT_SEARCH_PARAM_KEY } from '@/app/constants';
import { Country, DonationFormType, Language, PaymentMethod } from '@/app/enums';
import useAnalytics from '@/app/hooks/useAnalytics';
import { useToast } from '@/app/hooks/useToast';
import { isResponseError } from '@/app/utils/apiCommonUtils';
import { donationsApiForClient } from '@/app/utils/apiForClient';
import { countryConfig } from '@/app/utils/countryConfig';
import { formatPrice } from '@/app/utils/formatting';
import { getAmountInRespectiveCurrency, getDonationAmountIncludingTip } from '@/app/utils/numbers';
import * as Sentry from '@sentry/nextjs';

import {
  transformCommercialProjectDonationFormValuesToCreateDonationRequest,
  transformCreateDonationViolationsToDonationFormErrors,
} from '@/app/components/DonationForm/utils';

import { CustomAmount } from '@/app/components/DonationForm/donationFormParts/CustomAmount';

import ButtonRadioGroup, { ButtonRadioGroupOption } from '@/app/components/ButtonRadioGroup/ButtonRadioGroup';
import FormCheckbox from '@/app/components/Checkbox/FormCheckbox';
import Divider from '@/app/components/Divider/Divider';
import AddressFormPart from '@/app/components/DonationForm/donationFormParts/AddressFormPart';
import DonationFormHeader from '@/app/components/DonationForm/donationFormParts/DonationFormHeader';
import { SubmitButton } from '@/app/components/DonationForm/donationFormParts/SubmitButton';
import SubprojectParentBox from '@/app/components/DonationForm/donationFormParts/SubprojectParentBox';
import { TipFormSection } from '@/app/components/DonationForm/donationFormParts/TipFormSection';
import DonationFormDisclaimer, {
  DonationFormDisclaimerType,
} from '@/app/components/DonationFormDisclaimer/DonationFormDisclaimer';
import FormCard from '@/app/components/FormCard/FormCard';
import FormContainer from '@/app/components/FormContainer/FormContainer';
import FormInput from '@/app/components/Input/FormInput';
import RewardPicker from '@/app/components/RewardPicker/RewardPicker';
import FormTextarea from '@/app/components/Textarea/FormTextarea';

export interface CommercialProjectDonationFormValues extends DonationFormBaseValues {
  note: string;
  formType: DonationFormType.COMMERCIAL_PROJECT;
  organizationId?: string;
  email: string;
}

interface CommercialProjectDonationFormProps {
  project: Project;
  subproject?: Subproject;
  initialValues?: Partial<CommercialProjectDonationFormValues>;
  countrySelectOptions: SelectOption<Country>[];
  currentUser?: User;
  getPaymentMethodOptions: (
    donationMode: CreateDonationRequestModeEnum | null,
  ) => ButtonRadioGroupOption<PaymentMethod>[];
  onSuccess?: (donationId: string) => void;
  onFailure?: () => void;
  onCloseButtonClick?: () => void;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  rewards?: Reward[];
}

function CommercialProjectDonationForm({
  initialValues: defaultInitialValues,
  currentUser,
  project,
  subproject,
  countrySelectOptions,
  getPaymentMethodOptions,
  onCloseButtonClick,
  onSuccess,
  onFailure,
  shouldDisplayPrivacyConsentCheckbox,
  rewards,
}: CommercialProjectDonationFormProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const router = useRouter();
  const {
    donation: { pushDonationCompletedEvent },
  } = useAnalytics();
  const { showErrorToast, showGenericErrorToast } = useToast();

  const searchParams = useSearchParams();

  const subProject = searchParams.get(SUBPROJECT_SEARCH_PARAM_KEY) ?? undefined;
  const selectedRewardId = searchParams.get(REWARD_SEARCH_PARAM_KEY) ?? undefined;
  const selectedRewardVariantId = searchParams.get(REWARD_VARIANT_SEARCH_PARAM_KEY) ?? undefined;

  const isTipDisabled = useMemo(() => project.settings.hasTipsDisabled, [project.settings.hasTipsDisabled]);

  const selectedReward = useMemo(() => {
    if (!selectedRewardId) {
      return null;
    }

    const selectedReward = rewards?.find((reward) => reward.id === selectedRewardId);

    if (!selectedReward) {
      return null;
    }

    return selectedReward;
  }, [rewards, selectedRewardId]);

  const selectedRewardVariant = useMemo(() => {
    if (!selectedReward?.variants?.length) {
      return null;
    }

    const selectedRewardVariant = selectedReward.variants.find((variant) => variant.id === selectedRewardVariantId);

    if (!selectedRewardVariant) {
      return null;
    }

    return selectedRewardVariant;
  }, [selectedReward, selectedRewardVariantId]);

  const amountMinimum = useMemo(() => {
    if (!selectedReward) {
      return null;
    }

    if (!selectedReward.variants?.length || !selectedRewardVariant?.amountInCents) {
      return getAmountInRespectiveCurrency(selectedReward.amountInCents);
    }

    return getAmountInRespectiveCurrency(selectedRewardVariant.amountInCents);
  }, [selectedReward, selectedRewardVariant]);

  const areAddressFieldsRequired = !!selectedReward?.needsAddress;
  const isFirstNameRequired = !currentUser?.firstName;
  const isLastNameRequired = !currentUser?.lastName;
  const isNoteRequired = !!selectedReward?.noteFieldLabel;
  const isPhoneNumberRequired = project.settings.hasPhoneNumberRequired || areAddressFieldsRequired;

  // Inconsistent with charity collections (hidden if filled in)
  const isFirstNameFieldVisible = isFirstNameRequired || areAddressFieldsRequired;
  // Inconsistent with charity collections (hidden if filled in)
  const isLastNameFieldVisible = isLastNameRequired || areAddressFieldsRequired;
  const isEmailFieldVisible = !currentUser;
  const isCompanyNameFieldVisible = areAddressFieldsRequired;
  const isNoteFieldVisible = isNoteRequired;
  const isPhoneNumberFieldVisible = isPhoneNumberRequired;
  const areAddressFieldsVisible = areAddressFieldsRequired;

  const isAnyPersonalInformationFieldVisible =
    areAddressFieldsVisible ||
    isFirstNameFieldVisible ||
    isLastNameFieldVisible ||
    isCompanyNameFieldVisible ||
    isPhoneNumberFieldVisible ||
    isEmailFieldVisible;

  const validationSchema = useCommercialProjectDonationFormValidationSchema({
    isAuthenticated: !!currentUser,
    isFirstNameRequired,
    isLastNameRequired,
    isTipDisabled,
    areAddressFieldsRequired,
    amountMinimum: amountMinimum ?? undefined,
    isNoteRequired,
    isPhoneNumberRequired,
  });

  const initialValues = useMemo<CommercialProjectDonationFormValues>(() => {
    const nameOnReceipt =
      currentUser?.firstName && currentUser?.lastName
        ? `${currentUser?.firstName} ${currentUser?.lastName}`
        : currentUser?.fullName;

    return {
      formType: DonationFormType.COMMERCIAL_PROJECT,
      donationMode: CreateDonationRequestModeEnum.Single,
      paymentMethod: countryConfig.commercialProjects.defaultPaymentMethod,
      amount: amountMinimum ?? defaultInitialValues?.amount ?? 0,
      email: '',
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      certificateOfGoodness: false,
      donationReceipt: false,
      noNewsletter: false,
      agreedToShareContact: false,
      certificateRecipient: '',
      certificateDonor: '',
      nameOnReceipt: nameOnReceipt ?? '',
      phoneNumber: currentUser?.phoneNumber ?? '',
      streetAndHouseNumber: currentUser?.address?.street ?? '',
      city: currentUser?.address?.city ?? '',
      zipCode: currentUser?.address?.zip ?? '',
      tin: '',
      note: '',
      birthDate: undefined,
      // Workaround for the input not to swtich from uncontrolled to controlled (has to have a value)
      customTipAmount: '' as any as number,
      tipPercentage: project.settings.hasTipsDisabled
        ? 0
        : countryConfig.donationFormCommon.defaultTipForAmountBelowBreakpoint,
      country: (currentUser?.address?.country as Country) ?? countryConfig.country,
    };
  }, [defaultInitialValues, currentUser, amountMinimum]);

  const paymentMethodOptions = useMemo(() => {
    return getPaymentMethodOptions(CreateDonationRequestModeEnum.Single);
  }, [getPaymentMethodOptions]);

  const handleSubmit = useCallback(
    async (
      values: CommercialProjectDonationFormValues,
      helpers: FormikHelpers<CommercialProjectDonationFormValues>,
    ) => {
      if (!selectedRewardId) {
        return showErrorToast(t('messages.rewardIdMissing'));
      }

      if (selectedReward?.variants.length && !selectedRewardVariantId) {
        return showErrorToast(t('messages.rewardVariantIdMissing'));
      }

      try {
        const transformedValues = transformCommercialProjectDonationFormValuesToCreateDonationRequest(
          project.id,
          values,
          currentUser?.id,
          subProject,
          selectedRewardId,
          selectedRewardVariantId,
        );

        console.log('transformedValues', transformedValues);

        const response = await donationsApiForClient.postCreateDonation({
          createDonationRequest: transformedValues,
        });

        onSuccess?.(response.id);

        if (values.paymentMethod !== PaymentMethod.Card) {
          pushDonationCompletedEvent({
            donationId: response.id,
            requestDto: transformedValues,
            project,
            paymentMethod: values.paymentMethod,
          });
        }

        const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;
        const queryString = values.paymentMethod === PaymentMethod.Card ? '?redirectToGateway=1' : '';

        if (baseUrl?.endsWith('/')) {
          return router.push(`${baseUrl}prispevek/detail/${response.id}${queryString}`);
        }

        router.push(`${baseUrl}/prispevek/detail/${response.publicId}${queryString}`);
      } catch (error: unknown) {
        const isValidationError = isResponseError(error) && error.response.status === 422;

        if (!isValidationError) {
          console.log(error);
          console.log(values);
          Sentry.captureException(error);
        }

        onFailure?.();

        if (!isResponseError(error)) {
          return showGenericErrorToast();
        }

        switch (error.response.status) {
          case 422:
            const responseJson = await error.response.json();
            const transformedErrors = transformCreateDonationViolationsToDonationFormErrors(responseJson.violations);
            helpers.setErrors(transformedErrors);

            return showErrorToast(t('messages.422'));

          default:
            showGenericErrorToast();
        }
      }
      helpers.setSubmitting(false);
    },
    [
      onSuccess,
      onFailure,
      router,
      showErrorToast,
      showGenericErrorToast,
      project.id,
      isTipDisabled,
      currentUser,
      t,
      subProject,
      selectedRewardId,
      selectedRewardVariantId,
    ],
  );

  const collectionLink = useMemo(() => {
    const languageQuery = language === Language.EN ? `?lang=${language}` : '';

    return `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${project.slug}${languageQuery}`;
  }, [language, project.slug]);

  return (
    <div className="w-full">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values, setFieldValue, touched }) => (
          <FormCard onCloseButtonClick={onCloseButtonClick}>
            <FormContainer>
              <DonationFormHeader
                captionAtTheTop={t('components.DonationForm.contributeToThisProject')}
                collectionTitle={subproject?.content.name ?? project.content.currentName ?? ''}
                collectionImage={project.content.mainPhoto?.sizes.miniCropped.path}
                collectionLink={collectionLink}
              />

              {!!subproject && <SubprojectParentBox parentTitle={project.content.currentName} className="mb-[40px]" />}

              {!!rewards && (
                <RewardPicker
                  projectNumericId={project.numericId}
                  label={t('components.DonationForm.rewardSectionTitle')}
                  selectedRewardId={selectedRewardId}
                  selectedRewardVariantId={selectedRewardVariantId}
                  rewards={rewards}
                  className="mb-[64px]"
                />
              )}

              <div className="mt-[16px]">
                <CustomAmount
                  currentTip={values.tipPercentage}
                  label={t('components.DonationForm.higherAmountLabel')}
                  isTipTouched={!!touched.tipPercentage}
                  onTipChange={(value) => setFieldValue('tipPercentage', value)}
                />
                <span className="block mb-[56px]">{t('components.DonationForm.higherAmountNote')}</span>
              </div>

              {paymentMethodOptions.length > 1 && (
                <ButtonRadioGroup
                  name="paymentMethod"
                  label={t('components.DonationForm.paymentType')}
                  options={paymentMethodOptions}
                />
              )}

              <Divider className="mb-[56px] mt-[56px]" />

              {!currentUser && (
                <p className="my-[16px]">{t('components.DonationForm.personalInformationPrivacyDisclaimer')}</p>
              )}

              {isNoteFieldVisible && <FormTextarea name="note" rows={4} label={selectedReward?.noteFieldLabel ?? ''} />}

              {isEmailFieldVisible && (
                <FormInput
                  name="email"
                  label={t('components.DonationForm.email')}
                  note={t('components.DonationForm.emailNoteForCommercialProjects')}
                />
              )}

              {isFirstNameFieldVisible && <FormInput name="firstName" label={t('components.DonationForm.firstName')} />}

              {isLastNameFieldVisible && <FormInput name="lastName" label={t('components.DonationForm.lastName')} />}

              {isCompanyNameFieldVisible && (
                <FormInput name="nameOnReceipt" label={t('components.DonationForm.companyName')} />
              )}

              {isPhoneNumberFieldVisible && (
                <FormInput
                  name="phoneNumber"
                  label={t('components.DonationForm.phoneNumberLabel')}
                  placeholder={countryConfig.donationFormCommon.phoneNumberFieldPlaceholder}
                />
              )}

              {areAddressFieldsVisible && <AddressFormPart countrySelectOptions={countrySelectOptions} />}

              {!currentUser?.subscribesToNewsletter && (
                <FormCheckbox name="noNewsletter" label={t('components.DonationForm.noNewsletterCheckbox')} />
              )}
              {shouldDisplayPrivacyConsentCheckbox && (
                <FormCheckbox name="agreedToShareContact" label={t('components.DonationForm.privacyConsentCheckbox')} />
              )}

              {(!currentUser?.subscribesToNewsletter ||
                shouldDisplayPrivacyConsentCheckbox ||
                isAnyPersonalInformationFieldVisible ||
                isNoteFieldVisible) && <Divider className="my-[60px]" />}

              {!isTipDisabled && (
                <TipFormSection
                  amount={values.amount}
                  onTipPercentageChange={() => setFieldValue('customTipAmount', '')}
                />
              )}

              <SubmitButton isSubmitting={isSubmitting}>
                {t('components.DonationForm.submit', {
                  amount: formatPrice(
                    getDonationAmountIncludingTip(
                      values.amount || 0,
                      values.tipPercentage || 0,
                      values.customTipAmount,
                    ),
                  ),
                })}
              </SubmitButton>
              <DonationFormDisclaimer type={DonationFormDisclaimerType.COMMERCIAL_PROJECT} />
            </FormContainer>
          </FormCard>
        )}
      </Formik>
    </div>
  );
}

export default CommercialProjectDonationForm;
